import { useEffect, useState } from 'react';
import { updateLocationPayoutConfig } from '../../api/locations.api';
import { reportError } from '../../helpers/reportError';
import { PayoutConfigResponse } from '../../types/location.types';
// Direct file imports on purpose - the styles barrel triggers a circular init.
import { Div } from '../../styles/styles';
import { Button } from '../../components/button/button';
import { ModalPortal } from '../../components/modal/modalPortal';
import { AdminSpinner } from '../AdminSpinner/adminSpinner';
import { ErrorToast, SuccessToast } from '../Toasts/Toasts';
import { PayoutConfigFields } from './PayoutConfigFields';
import {
  payoutServiceLabel,
  toPayoutConfigsPayload,
  validatePayoutConfigs,
} from './payoutConfig.helpers';

/**
 * Edits one service's payout config on its own, through the dedicated
 * payout-config endpoint. Only the edited service is sent, which the API reads
 * as "leave every other service alone".
 */
export const PayoutConfigModal = ({
  modalIsOpen,
  closeModal,
  locationId,
  config,
  onSaved,
  readOnly,
}: {
  modalIsOpen: boolean;
  closeModal: () => void;
  locationId: string | number;
  config: PayoutConfigResponse | null;
  onSaved?: () => void | Promise<void>;
  readOnly?: boolean;
}) => {
  const [draft, setDraft] = useState<PayoutConfigResponse | null>(config);
  const [loading, setLoading] = useState(false);

  // Reseed whenever a different card is opened, and discard edits on close.
  useEffect(() => {
    setDraft(config);
  }, [config, modalIsOpen]);

  const handleSave = async () => {
    if (!draft) return;

    const error = validatePayoutConfigs([draft]);
    if (error) return ErrorToast(error);

    try {
      setLoading(true);
      const { data } = await updateLocationPayoutConfig(locationId, {
        configs: toPayoutConfigsPayload([draft]),
      });
      SuccessToast(data.message);
      await onSaved?.();
      closeModal();
    } catch (err) {
      reportError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalPortal
      isOpen={modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={draft ? payoutServiceLabel(draft) : 'Payout Configuration'}
      modalOverflow="visible"
      width="600px"
    >
      {loading && <AdminSpinner overflowBorderRadius="30px" />}

      {draft && (
        <Div $flex $column $gap="24px">
          <PayoutConfigFields
            config={draft}
            savedConfig={config ?? undefined}
            disabled={readOnly}
            onChange={(patch) =>
              setDraft((prev) => (prev ? { ...prev, ...patch } : prev))
            }
          />

          {!readOnly && (
            <Button $selected $w="100%" onClick={handleSave}>
              Save
            </Button>
          )}
        </Div>
      )}
    </ModalPortal>
  );
};
